import {
	FORMULA_OPERATIONS as FP,
	GeometryTypes,
	IAdjustHandlerDirection,
	PathOperationTypes,
	ShapeType,
} from "../widgets/interfaces/model/enumerations/index";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";

/**
 * swooshArrow preset definition
 * ECMA-376, Third Edition, Part 1 - Fundamentals And Markup Language Reference
 * /OfficeOpenXML-DrawingMLGeometries/presetShapeDefinitions.xml
 */
export const swooshArrow: IPresetGeometry = {
	ahLst: [
		{
			directionX: IAdjustHandlerDirection.REVERSE,
			gdRefX: "adj2", maxX: "maxAdj2", minX: 0, pos: { x: "xB", y: "yB" },
		},
		{ gdRefY: "adj1", maxY: "75000", minY: 1, pos: { x: "xF", y: "yF" } },
	],
	avList: [
		{ name: "adj1", fmla: { operation: FP.LiteralValue, operands: [25000] } },
		{ name: "adj2", fmla: { operation: FP.LiteralValue, operands: [16667] } },
	],

	gdLst: [
		{ name: "a1", fmla: { operation: FP.PinTo, operands: [1, "adj1", 75000] } },
		{ name: "maxAdj2", fmla: { operation: FP.MultiplyDivide, operands: [70000, "w", "ss"] } },
		{ name: "a2", fmla: { operation: FP.PinTo, operands: [0, "adj2", "maxAdj2"] } },
		{ name: "ad1", fmla: { operation: FP.MultiplyDivide, operands: ["h", "a1", "100000"] } },
		{ name: "ad2", fmla: { operation: FP.MultiplyDivide, operands: ["ss", "a2", "100000"] } },
		{ name: "xB", fmla: { operation: FP.AddSubtract, operands: ["r", 0, "ad2"] } },
		{ name: "yB", fmla: { operation: FP.AddSubtract, operands: ["t", "ssd8", 0] } },
		{ name: "alfa", fmla: { operation: FP.MultiplyDivide, operands: ["cd4", 1, 14] } },
		{ name: "dx0", fmla: { operation: FP.Tangent, operands: ["ssd8", "alfa"] } },
		{ name: "xC", fmla: { operation: FP.AddSubtract, operands: ["xB", 0, "dx0"] } },
		{ name: "dx1", fmla: { operation: FP.Tangent, operands: ["ad1", "alfa"] } },
		{ name: "yF", fmla: { operation: FP.AddSubtract, operands: ["yB", "ad1", 0] } },
		{ name: "xF", fmla: { operation: FP.AddSubtract, operands: ["xB", "dx1", 0] } },
		{ name: "xE", fmla: { operation: FP.AddSubtract, operands: ["xF", "dx0", 0] } },
		{ name: "yE", fmla: { operation: FP.AddSubtract, operands: ["yF", "ssd8", 0] } },
		{ name: "dy2", fmla: { operation: FP.AddSubtract, operands: ["yE", 0, "t"] } },
		{ name: "dy22", fmla: { operation: FP.MultiplyDivide, operands: ["dy2", 1, 2] } },
		{ name: "dy3", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 20] } },
		{ name: "yD", fmla: { operation: FP.AddSubtract, operands: ["t", "dy22", "dy3"] } },
		{ name: "dy4", fmla: { operation: FP.MultiplyDivide, operands: ["hd6", 1, 1] } },
		{ name: "yP1", fmla: { operation: FP.AddSubtract, operands: ["hd6", "dy4", 0] } },
		{ name: "xP1", fmla: { operation: FP.LiteralValue, operands: ["wd6"] } },
		{ name: "dy5", fmla: { operation: FP.MultiplyDivide, operands: ["hd6", 1, 2] } },
		{ name: "yP2", fmla: { operation: FP.AddSubtract, operands: ["yF", "dy5", 0] } },
		{ name: "xP2", fmla: { operation: FP.LiteralValue, operands: ["wd4"] } },
	],
	pathLst: [{
		operations: [
			{ pt: { x: "l", y: "b" }, type: PathOperationTypes.MOVE_TO },
			{
				pts: { 0: { x: "xP1", y: "yP1" }, 1: { x: "xB", y: "yB" } },
				type: PathOperationTypes.QUAD_BEZ_TO,
			},
			{ pt: { x: "xC", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "r", y: "yD" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xE", y: "yE" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xF", y: "yF" }, type: PathOperationTypes.LINE_TO },
			{
				pts: { 0: { x: "xP2", y: "yP2" }, 1: { x: "l", y: "b" } },
				type: PathOperationTypes.QUAD_BEZ_TO,
			},
			{ type: PathOperationTypes.CLOSE },
		],
	}],
	presetShape: ShapeType.swooshArrow,
	rect: { l: "l", t: "t", r: "r", b: "b" },
	type: GeometryTypes.PRESET,
};

/**
 * Microsoft open xml preset definition (for Reference purpose only)
 *
 * <swooshArrow>
 *    <avLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *      <gd name="adj1" fmla="val 25000" />
 *      <gd name="adj2" fmla="val 16667" />
 *    </avLst>
 *    <gdLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *      <gd name="a1" fmla="pin 1 adj1 75000" />
 *      <gd name="maxAdj2" fmla="* / 70000 w ss" />
 *      <gd name="a2" fmla="pin 0 adj2 maxAdj2" />
 *      <gd name="ad1" fmla="* / h a1 100000" />
 *      <gd name="ad2" fmla="* / ss a2 100000" />
 *      <gd name="xB" fmla="+- r 0 ad2" />
 *      <gd name="yB" fmla="+- t ssd8 0" />
 *      <gd name="alfa" fmla="* / cd4 1 14" />
 *      <gd name="dx0" fmla="tan ssd8 alfa" />
 *      <gd name="xC" fmla="+- xB 0 dx0" />
 *      <gd name="dx1" fmla="tan ad1 alfa" />
 *      <gd name="yF" fmla="+- yB ad1 0" />
 *      <gd name="xF" fmla="+- xB dx1 0" />
 *      <gd name="xE" fmla="+- xF dx0 0" />
 *      <gd name="yE" fmla="+- yF ssd8 0" />
 *      <gd name="dy2" fmla="+- yE 0 t" />
 *      <gd name="dy22" fmla="* / dy2 1 2" />
 *      <gd name="dy3" fmla="* / h 1 20" />
 *      <gd name="yD" fmla="+- t dy22 dy3" />
 *      <gd name="dy4" fmla="* / hd6 1 1" />
 *      <gd name="yP1" fmla="+- hd6 dy4 0" />
 *      <gd name="xP1" fmla="val wd6" />
 *      <gd name="dy5" fmla="* / hd6 1 2" />
 *      <gd name="yP2" fmla="+- yF dy5 0" />
 *      <gd name="xP2" fmla="val wd4" />
 *    </gdLst>
 *    <ahLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *      <ahXY gdRefY="adj1" minY="1" maxY="75000">
 *        <pos x="xF" y="yF" />
 *      </ahXY>
 *      <ahXY gdRefX="adj2" minX="0" maxX="maxAdj2">
 *        <pos x="xB" y="yB" />
 *      </ahXY>
 *    </ahLst>
 *    <cxnLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *      <cxn ang="cd4">
 *        <pos x="l" y="b" />
 *      </cxn>
 *      <cxn ang="3cd4">
 *        <pos x="xC" y="t" />
 *      </cxn>
 *      <cxn ang="0">
 *        <pos x="r" y="yD" />
 *      </cxn>
 *      <cxn ang="cd4">
 *        <pos x="xE" y="yE" />
 *      </cxn>
 *    </cxnLst>
 *    <rect l="l" t="t" r="r" b="b" xmlns="http://schemas.openxmlformats.org/drawingml/2006/main" />
 *    <pathLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
 *      <path>
 *        <moveTo>
 *          <pt x="l" y="b" />
 *        </moveTo>
 *        <quadBezTo>
 *          <pt x="xP1" y="yP1" />
 *          <pt x="xB" y="yB" />
 *        </quadBezTo>
 *        <lnTo>
 *          <pt x="xC" y="t" />
 *        </lnTo>
 *        <lnTo>
 *          <pt x="r" y="yD" />
 *        </lnTo>
 *        <lnTo>
 *          <pt x="xE" y="yE" />
 *        </lnTo>
 *        <lnTo>
 *          <pt x="xF" y="yF" />
 *        </lnTo>
 *        <quadBezTo>
 *          <pt x="xP2" y="yP2" />
 *          <pt x="l" y="b" />
 *        </quadBezTo>
 *        <close />
 *      </path>
 *    </pathLst>
 *  </swooshArrow>
 */
